/**
 * Canonical Yadid Advisory teaching content for the two continuous dynamics
 * that run across all four Embrace movements (Listen · Reveal · Reimagine ·
 * Guide). These are firm-wide reflective conditions — NOT additional phases.
 *
 * The wording is intentionally verbatim from advisory-partner-approved copy
 * (Feb 2026 revision). Do not paraphrase in code; if the language ever needs
 * to shift, edit here so every surface (Method utility, Engagement Embrace
 * tab, Diagnostic Memos) stays coherent.
 *
 * Architecture note: keeping this static in a JS module for now so the
 * treatment ships this release. When Managing-Principal governance +
 * controlled versioning is desired, this exact shape can be moved onto the
 * `/method/template` endpoint under `template.continuous_dynamics` without
 * touching the consumers.
 */

export const CONTINUOUS_DYNAMICS = {
  connections: {
    key: "connections",
    /** Framework label — should match the current framework_labels.top literal
     *  Yadid uses for the surrounding-label surface. */
    label: "Connections and Relationships",
    /** Compact single-line label used for the card eyebrow + tooltip title */
    shortLabel: "Connections & Relationships",
    framingQuestion:
      "What is the current relational reality of this engagement, and what does it make possible or place at risk?",
    explainer:
      "This dynamic examines the trust, access, influence, power, stakeholder alignment, and partnership conditions surrounding the engagement. It helps the advisor see which relationships make progress possible, which ones place the work at risk, whose voice is missing, and where connection, mediation, or trust-building is required.",
    groupings: [
      {
        key: "current_reality",
        label: "Current Reality",
        prompts: [
          "Which relationships currently carry the engagement?",
          "Where is trust strong, fragile, borrowed, or absent?",
        ],
      },
      {
        key: "system_dynamics",
        label: "System Dynamics",
        prompts: [
          "Who holds influence, access, legitimacy, or veto power?",
          "Whose voice, authority, or lived experience is missing?",
          "Where do stakeholders lack shared language, shared expectations, or a shared definition of success?",
          "Where are power, risk, or responsibility unevenly distributed?",
        ],
      },
      {
        key: "strategic_need",
        label: "Strategic Need",
        prompts: [
          "Where is mediation, translation, convening, or trust repair needed?",
          "What institution, community, network, coalition, or existing initiative could strengthen this work?",
          "What connection could prevent duplication or create greater leverage?",
        ],
      },
      {
        key: "next_move",
        label: "Next Move",
        prompts: [
          "Which relationship should be deepened, repaired, activated, or released next?",
        ],
      },
    ],
    workingNotesPrompt:
      "Record emerging relationship signals, shifts in trust, stakeholder movements, access pathways, partnership opportunities, and relational risks that may affect the engagement.",
  },

  leadership: {
    key: "leadership",
    label: "Leadership and Storytelling",
    shortLabel: "Leadership & Storytelling",
    framingQuestion:
      "What leadership reality and governing narrative are shaping this engagement right now?",
    explainer:
      "This dynamic examines the leadership judgment, values, decisions, alignment, narrative, and evidence shaping the engagement. It helps the advisor determine what must become clearer, more credible, more compelling, or more courageously led for the work to move forward.",
    groupings: [
      {
        key: "current_reality",
        label: "Current Reality",
        prompts: [
          "Where is leadership judgment currently being tested?",
          "What narrative is shaping stakeholder behavior right now?",
        ],
      },
      {
        key: "system_dynamics",
        label: "System Dynamics",
        prompts: [
          "Who must understand, endorse, authorize, or champion the next move?",
          "Where are values, decisions, and behavior misaligned?",
          "Where are different audiences hearing different meanings from the same message?",
          "What decision is being delayed, avoided, or obscured?",
        ],
      },
      {
        key: "strategic_need",
        label: "Strategic Need",
        prompts: [
          "What hopeful and credible future is this engagement asking people to believe is possible?",
          "What is the clearest sentence that explains why this work matters now?",
          "What evidence, proof, or wider measure of value is needed to make the case credible?",
          "What story from the work should be captured and shared to build confidence or momentum?",
        ],
      },
      {
        key: "next_move",
        label: "Next Move",
        prompts: [
          "What decision, conversation, rehearsal, or message should happen next?",
        ],
      },
    ],
    workingNotesPrompt:
      "Record emerging leadership judgments, decisions, alignment shifts, narrative changes, evidence needs, audience reactions, and moments requiring executive courage or communication.",
  },
};

/** Ordered pair used by the arcs surface — top slot then bottom slot. */
export const CONTINUOUS_DYNAMIC_ORDER = ["connections", "leadership"];
